import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Edit, 
  Paperclip, 
  FileText, 
  Download, 
  Trash2, 
  Upload, 
  MessageSquare,
  Package,
  AlertCircle,
  CheckSquare,
  History,
  RefreshCw,
  Clock,
  UserCheck
} from 'lucide-react';
import { COLUMNAS, PRIORIDADES, calcularTiempoTranscurrido, obtenerColorTiempo } from '@/utils/tareaUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TareaDetailModal({
  open,
  onOpenChange,
  tarea,
  onEdit,
  onToggleSubtarea,
  onAgregarComentario,
  nuevoComentario,
  setNuevoComentario,
  onUploadFile,
  uploadingFile,
  onDeleteAdjunto,
  onAplicarMateriales,
  aplicandoMateriales,
}) {
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [subtareasModificadas, setSubtareasModificadas] = useState(() => tarea?.subtareas || []);
  const [haycambiosPendientes, setHaycambiosPendientes] = useState(false);
  const [actualizandoSubtareas, setActualizandoSubtareas] = useState(false);

  // Resetear estado cuando cambie la tarea
  useEffect(() => {
    if (tarea) {
      setSubtareasModificadas(tarea.subtareas || []);
      setHaycambiosPendientes(false);
    }
  }, [tarea]);

  if (!tarea) return null;

  const handleToggleSubtareaLocal = (subtareaId) => {
    const nuevasSubtareas = subtareasModificadas.map(st => 
      st.id === subtareaId ? { ...st, completada: !st.completada } : st
    );
    setSubtareasModificadas(nuevasSubtareas);
    setHaycambiosPendientes(true);
  };

  const handleActualizarSubtareas = async () => {
    setActualizandoSubtareas(true);
    
    // Encontrar subtareas que cambiaron
    const cambios = subtareasModificadas.filter((stModificada, index) => {
      const stOriginal = tarea.subtareas[index];
      return stOriginal && stModificada.completada !== stOriginal.completada;
    });

    // Ejecutar los toggles para las subtareas que cambiaron
    for (const st of cambios) {
      await onToggleSubtarea(tarea.id, st.id);
    }

    setActualizandoSubtareas(false);
    setHaycambiosPendientes(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">{tarea.titulo}</DialogTitle>
            <Badge className={PRIORIDADES[tarea.prioridad].color}>
              {PRIORIDADES[tarea.prioridad].icon} {PRIORIDADES[tarea.prioridad].label}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Tiempo transcurrido */}
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <Label className="text-sm text-gray-600">Tiempo transcurrido desde creación</Label>
                <p className={`text-lg font-bold ${obtenerColorTiempo(tarea.created_at)}`}>
                  {calcularTiempoTranscurrido(tarea.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Info básica */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Estado</Label>
              <p className="font-semibold">{COLUMNAS.find(c => c.id === tarea.estado)?.titulo}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Creado por</Label>
              <p className="font-semibold">{tarea.creado_por}</p>
            </div>
            {tarea.responsable_nombre && (
              <div>
                <Label className="text-sm text-gray-600">Responsable</Label>
                <p className="font-semibold">{tarea.responsable_nombre}</p>
              </div>
            )}
            {tarea.revisor_nombre && (
              <div className="col-span-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-600" />
                  <div>
                    <Label className="text-sm text-purple-700">Revisor Asignado</Label>
                    <p className="font-semibold text-purple-900">{tarea.revisor_nombre}</p>
                  </div>
                </div>
              </div>
            )}
            {tarea.lote_nombre && (
              <div>
                <Label className="text-sm text-gray-600">Lote</Label>
                <p className="font-semibold">{tarea.lote_nombre}</p>
              </div>
            )}
            {tarea.fecha_planificada && (
              <div>
                <Label className="text-sm text-gray-600">Fecha Planificada</Label>
                <p className="font-semibold">{tarea.fecha_planificada}</p>
              </div>
            )}
          </div>

          {tarea.descripcion && (
            <div>
              <Label className="text-sm text-gray-600">Descripción</Label>
              <p className="mt-1">{tarea.descripcion}</p>
            </div>
          )}

          {/* Subtareas */}
          {tarea.subtareas && tarea.subtareas.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-gray-600">Subtareas</Label>
                {haycambiosPendientes && (
                  <Button
                    size="sm"
                    onClick={handleActualizarSubtareas}
                    disabled={actualizandoSubtareas}
                    className="bg-emerald-600 hover:bg-emerald-700 h-8"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${actualizandoSubtareas ? 'animate-spin' : ''}`} />
                    {actualizandoSubtareas ? 'Actualizando...' : 'Actualizar'}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {subtareasModificadas.map((st) => (
                  <div key={st.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Checkbox
                      checked={st.completada}
                      onCheckedChange={() => handleToggleSubtareaLocal(st.id)}
                    />
                    <span className={st.completada ? 'line-through text-gray-500' : ''}>
                      {st.titulo}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Archivos Adjuntos */}
          <div>
            <Label className="text-sm text-gray-600 mb-2 block flex items-center">
              <Paperclip className="w-4 h-4 mr-1" />
              Archivos Adjuntos
            </Label>
            <div className="space-y-2 mb-3">
              {tarea.adjuntos && tarea.adjuntos.length > 0 ? (
                tarea.adjuntos.map((adj, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">{adj.nombre_original}</p>
                        {adj.subido_en && (
                          <p className="text-xs text-gray-500">
                            {new Date(adj.subido_en).toLocaleString('es-ES')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8"
                        onClick={() => window.open(`${BACKEND_URL}${adj.url}`, '_blank')}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-red-600"
                        onClick={() => onDeleteAdjunto(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No hay archivos adjuntos</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={onUploadFile}
                disabled={uploadingFile}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => document.getElementById('file-upload').click()}
                disabled={uploadingFile}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingFile ? 'Subiendo...' : 'Adjuntar Archivo'}
              </Button>
              <span className="text-xs text-gray-500">Máx. 10MB</span>
            </div>
          </div>

          {/* Comentarios */}
          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Comentarios</Label>
            <div className="space-y-3 mb-3">
              {tarea.comentarios && tarea.comentarios.length > 0 ? (
                tarea.comentarios.map((com) => (
                  <div key={com.id} className="p-3 bg-blue-50 rounded">
                    <p className="text-sm font-semibold">{com.usuario}</p>
                    <p className="text-sm">{com.texto}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(com.created_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No hay comentarios</p>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Escribe un comentario..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onAgregarComentario();
                  }
                }}
              />
              <Button onClick={onAgregarComentario}>
                <MessageSquare className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Materiales Requeridos e Inventario */}
          {tarea.materiales_requeridos && tarea.materiales_requeridos.length > 0 && (
            <div>
              <Label className="text-sm text-gray-600 mb-2 block flex items-center">
                <Package className="w-4 h-4 mr-1" />
                Materiales Requeridos
              </Label>
              <div className="space-y-2 mb-3">
                {tarea.materiales_requeridos.map((mat, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm font-medium">{mat.material_nombre}</p>
                      <p className="text-xs text-gray-500">Cantidad: {mat.cantidad} {mat.unidad_medida}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {tarea.estado === 'finalizado' && !tarea.materiales_utilizados && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <p className="text-sm font-semibold text-yellow-800">
                      Materiales pendientes de aplicar
                    </p>
                  </div>
                  <p className="text-xs text-yellow-700 mb-2">
                    Esta tarea está finalizada pero los materiales aún no se han descontado del inventario.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => onAplicarMateriales(tarea.id)}
                    disabled={aplicandoMateriales}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    {aplicandoMateriales ? 'Aplicando...' : 'Aplicar Materiales al Inventario'}
                  </Button>
                </div>
              )}
              
              {tarea.materiales_utilizados && (
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-xs text-green-700 flex items-center">
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Materiales ya descontados del inventario
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Historial de Cambios */}
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMostrarHistorial(!mostrarHistorial)}
              className="mb-2"
            >
              <History className="w-4 h-4 mr-2" />
              {mostrarHistorial ? 'Ocultar' : 'Ver'} Historial de Cambios
            </Button>
            
            {mostrarHistorial && (
              <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                {tarea.historial && tarea.historial.length > 0 ? (
                  tarea.historial.map((cambio, index) => (
                    <div key={index} className="p-2 bg-gray-50 rounded text-xs">
                      <p className="font-semibold text-gray-700">{cambio.usuario}</p>
                      <p className="text-gray-600">{cambio.accion}</p>
                      <p className="text-gray-400">
                        {new Date(cambio.fecha).toLocaleString('es-ES')}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No hay cambios registrados</p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          <Button onClick={() => {
            onOpenChange(false);
            onEdit(tarea);
          }} className="bg-emerald-600 hover:bg-emerald-700">
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
